import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AuditForm from "@/components/services/AuditForm";

const testimonials = [
  {
    label: "Jasmeet — Founder, Dialogue",
    to: "/work/dialogue",
    quote:
      "Parastou is one of the best UX designers I worked with. She understands how to build the right user experience natively and select the right wording that resonates with the user, and was very available if I had to discuss something. Overall quality was top. I definitely got more than I initially hoped for.",
  },
  {
    label: "Tyrina — BNSI Data Portal",
    to: "/work/bnsi-redesign",
    quote:
      "Absolute pleasure working with Parastou. Great communication and high level execution.",
  },
];

const offers = [
  {
    title: "UX Audit",
    price: "$1,500 · 1 week",
    desc: "A focused review of your product's key flows, with findings ranked by what's costing you the most.",
    get: "A prioritized findings document with annotated screens, plus a call to walk through it.",
    who: "Founders who know something's wrong but not what.",
  },
  {
    title: "Fix Sprint",
    price: "$2,800 · 2–3 weeks",
    desc: "The audit tells you what's broken. This fixes it.",
    get: "Redesigned screens your engineers can build from — up to three flows, with one round of revisions. Not a doc with recommendations, actual UI.",
    who: "Teams with engineers but no designer, where the findings would otherwise sit in a doc forever. Follows on naturally from an audit, but I'll do it standalone if you already know what's wrong.",
  },
  {
    title: "UX Partner",
    price: "$4,000/month",
    desc: "Ongoing design across everything you're shipping, not one project.",
    get: "Continuous design work, on your roadmap, at your pace.",
    who: "Teams shipping fast enough that one-off engagements don't keep up.",
  },
];

export default function Services() {
  return (
    <div
      className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]"
      data-testid="services-page"
    >
      <Nav />

      <main>
        {/* Offers — three rungs of one ladder, sections aligned via subgrid */}
        <section
          className="border-b border-[#0A0A0A] bg-gradient-to-b from-[#F5F3FF] to-[#FFFFFF]"
          data-testid="services-offers"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-10 md:pt-14 pb-14 md:pb-20">
            {/* Compact positioning header */}
            <div className="mb-8 md:mb-10">
              <p className="label-mono text-[#4C1D95] mb-3">/ Services</p>
              <h1 className="font-display text-4xl md:text-6xl tracking-tighter">
                Work With Me<span className="text-[#4C1D95]">.</span>
              </h1>
              <p className="text-base md:text-lg leading-relaxed text-[#595959] max-w-2xl mt-4">
                Three ways to work together, from a one-week audit to an ongoing
                partnership — pick the rung that matches where you are.
              </p>
            </div>

            {/* Three uniform cards */}
            <p className="label-mono text-[#595959] mb-5">
              The Clarity Ladder — audit, sprint, partner
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-0 md:gap-x-8 gap-y-6 md:gap-y-0 md:[grid-template-rows:repeat(10,auto)]">
              {offers.map((o, i) => (
                <div
                  key={o.title}
                  className="grid grid-rows-[repeat(10,auto)] gap-y-3 md:[grid-row:span_10] md:grid-rows-subgrid border border-[#0A0A0A] bg-white p-7 md:p-8"
                  data-testid={`services-card-${i}`}
                >
                  {/* 1 — Service name */}
                  <h2 className="font-display text-2xl md:text-3xl tracking-tighter">
                    {o.title}
                  </h2>

                  {/* 2 — Price and duration (figure, not label) */}
                  <p
                    className="text-[1.25rem] leading-none font-medium tracking-tight text-[#4C1D95]"
                    data-testid={`services-card-price-${i}`}
                  >
                    {o.price}
                  </p>

                  {/* 3 — Lead description (the hook) */}
                  <p className="text-lg leading-snug text-[#0A0A0A]">{o.desc}</p>

                  {/* 4 — Rule */}
                  <hr className="border-0 border-t border-[#0A0A0A]/10 w-full" />

                  {/* 5 — YOU GET label */}
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0A0A0A]">
                    You get
                  </p>

                  {/* 6 — YOU GET body */}
                  <p className="text-sm md:text-base leading-relaxed text-[#52525B]">
                    {o.get}
                  </p>

                  {/* 7 — Rule */}
                  <hr className="border-0 border-t border-[#0A0A0A]/10 w-full" />

                  {/* 8 — BEST FOR label */}
                  <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[#0A0A0A]">
                    Best for
                  </p>

                  {/* 9 — BEST FOR body */}
                  <p className="text-sm md:text-base leading-relaxed text-[#52525B]">
                    {o.who}
                  </p>

                  {/* 10 — CTA (identical across all three) */}
                  <a
                    href="#book"
                    className="mt-2 self-end"
                    data-testid={`services-card-cta-${i}`}
                  >
                    <span className="flex items-center justify-center gap-3 bg-[#4C1D95] text-white px-6 py-3.5 label-mono border border-[#4C1D95] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] transition-colors">
                      Get in touch <ArrowUpRight size={16} strokeWidth={1.5} />
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section
          className="border-b border-[#0A0A0A]"
          data-testid="services-testimonials"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-16 md:py-24">
            <p className="label-mono text-[#595959] mb-10">/ What clients say</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {testimonials.map((t, i) => (
                <figure
                  key={t.label}
                  className="border border-[#0A0A0A] bg-white p-7 md:p-9 flex flex-col"
                  data-testid={`services-testimonial-${i}`}
                >
                  <blockquote className="text-lg md:text-xl leading-relaxed font-normal text-[#0A0A0A]">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-6">
                    <Link
                      to={t.to}
                      className="label-mono text-[#4C1D95] hover:text-[#0A0A0A] transition-colors inline-flex items-center gap-1.5"
                      data-testid={`services-testimonial-link-${i}`}
                    >
                      {t.label}
                      <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* Credibility strip */}
        <section
          className="border-b border-[#0A0A0A] bg-[#0A0A0A] text-white"
          data-testid="services-credibility"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-6 md:py-7 flex flex-wrap items-center justify-between gap-4">
            <p className="label-mono text-[#DDD6FE] tracking-[0.12em]">
              Enterprise instrument software · Fintech · SaaS platforms
            </p>
            <a
              href="/#work"
              className="group inline-flex items-center gap-2 label-mono text-white hover:text-[#C4B5FD] transition-colors"
              data-testid="services-see-work"
            >
              See the work
              <ArrowUpRight
                size={15}
                strokeWidth={1.5}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </section>

        {/* Final CTA */}
        <section
          id="book"
          className="border-b border-[#0A0A0A] bg-gradient-to-b from-[#F5F3FF] to-[#EDE9FE] scroll-mt-16"
          data-testid="services-cta-bottom"
        >
          <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-20 md:py-28 grid grid-cols-12 gap-y-10 md:gap-x-16 items-center">
            <div className="col-span-12 lg:col-span-5">
              <p className="label-mono text-[#4C1D95] mb-6">/ Get started</p>
              <h2 className="font-display text-4xl md:text-6xl tracking-tighter">
                Let&apos;s make it{" "}
                <span className="text-[#4C1D95]">clear</span>.
              </h2>
              <p className="text-lg leading-relaxed text-[#3F3F46] max-w-md mt-6">
                Fixed-price audit, no budget forms. Drop your email and what&apos;s
                feeling tangled — I reply within 24 hours.
              </p>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <AuditForm />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
